import { useAuth0 } from "@auth0/auth0-react";
import React, {
  createContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useContext
} from "react";
import { AccessTokenHelper } from "utils/token";

const stub = (): never => {
  throw new Error(
    "You forgot to wrap your component in <LoggedInUserProvider>. Is this a unit test?"
  );
};

export const LoggedInUserContext = createContext<LoggedInUserContextInterface>({
  get currentUser() {
    stub();
    return null;
  },
  updatePermissions: stub
});

export const useLoggedInUser = () => useContext(LoggedInUserContext);

export interface LoggedInUserContextInterface {
  currentUser: AccessTokenHelper;
  updatePermissions: () => void;
}

export const LoggedInUserProvider: React.FunctionComponent = ({ children }) => {
  const { getAccessTokenSilently, isLoading } = useAuth0();
  const [accessToken, setAccessToken] = useState(null);
  const [ignoreCache, setIgnoreCache] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      getAccessTokenSilently({ ignoreCache })
        .then(setAccessToken)
        .finally(() => {
          if (ignoreCache) {
            setIgnoreCache(false);
          }
        });
    }
  }, [setAccessToken, getAccessTokenSilently, ignoreCache, isLoading]);

  const updatePermissions = useCallback(
    () => setIgnoreCache(true),
    [setIgnoreCache]
  );

  const contextValue = useMemo(
    () =>
      ({
        updatePermissions,
        currentUser: accessToken
          ? new AccessTokenHelper(accessToken)
          : new AccessTokenHelper(null)
      } as LoggedInUserContextInterface),
    [accessToken, updatePermissions]
  );

  return (
    <LoggedInUserContext.Provider value={contextValue}>
      {children}
    </LoggedInUserContext.Provider>
  );
};
